<script>
import { login } from '../../api/auth';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { required, email } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { SESSION_STORAGE_KEYS } from 'dashboard/constants/sessionStorage';
import SessionStorage from 'shared/helpers/sessionStorage';
import { useBranding } from 'shared/composables/useBranding';
import axios from 'axios';

import Spinner from 'shared/components/Spinner.vue';
import MfaVerification from 'dashboard/components/auth/MfaVerification.vue';

const ERROR_MESSAGES = {
  'no-account-found': 'LOGIN.OAUTH.NO_ACCOUNT_FOUND',
  'business-account-only': 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY',
  'saml-authentication-failed': 'LOGIN.SAML.API.ERROR_MESSAGE',
  'saml-not-enabled': 'LOGIN.SAML.API.ERROR_MESSAGE',
};

const IMPERSONATION_URL_SEARCH_KEY = 'impersonation';

export default {
  components: {
    Spinner,
    MfaVerification,
  },
  props: {
    ssoAuthToken: { type: String, default: '' },
    ssoAccountId: { type: String, default: '' },
    ssoConversationId: { type: String, default: '' },
    email: { type: String, default: '' },
    authError: { type: String, default: '' },
  },
  setup() {
    const { replaceInstallationName } = useBranding();
    return {
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      credentials: {
        email: '',
        password: '',
      },
      loginApi: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
      mfaRequired: false,
      mfaToken: null,
      activeTab: 'password',
      magicLinkEmail: '',
      magicLinkSent: false,
      magicLinkLoading: false,
      statIndex: 0,
      statVisible: true,
      statsData: [
        { num: '98.4%', label: 'Customer Satisfaction' },
        { num: '2.4m', label: 'Avg. Response Time' },
        { num: '847', label: 'Tickets Resolved Today' },
        { num: '12,483', label: 'Active Support Agents' },
        { num: '99.9%', label: 'Platform Uptime' },
      ],
    };
  },
  validations() {
    return {
      credentials: {
        password: { required },
        email: { required, email },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
    magicLinkMode() {
      return this.activeTab === 'magic';
    },
    allowedLoginMethods() {
      return window.chatwootConfig.allowedLoginMethods || ['email'];
    },
    showGoogleOAuth() {
      return (
        this.allowedLoginMethods.includes('google_oauth') &&
        Boolean(window.chatwootConfig.googleOAuthClientId)
      );
    },
    showSignupLink() {
      return window.chatwootConfig.signupEnabled === 'true';
    },
    showSamlLogin() {
      return this.allowedLoginMethods.includes('saml');
    },
    googleAuthUrl() {
      const clientId = window.chatwootConfig.googleOAuthClientId;
      const redirectUri = window.chatwootConfig.googleOAuthCallbackUrl;
      return `https://accounts.google.com/o/oauth2/auth/oauthchooseaccount?${new URLSearchParams({ client_id: clientId, redirect_uri: redirectUri, response_type: 'code', scope: 'email profile' })}`;
    },
  },
  created() {
    if (this.ssoAuthToken) {
      this.submitLogin();
    }
    if (this.authError) {
      const messageKey = ERROR_MESSAGES[this.authError] ?? 'LOGIN.API.UNAUTH';
      const translatedMessage = this.getTranslatedMessage(messageKey);
      useAlert(translatedMessage);
      this.requestIdleCallbackPolyfill(() => {
        const { query } = this.$route;
        this.$router.replace({ query: { ...query, error: undefined } });
      });
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initConversationFlow();
      this.initRotatingStats();
    });
  },
  beforeUnmount() {
    if (this.statsTimer) clearTimeout(this.statsTimer);
  },
  methods: {
    getTranslatedMessage(key) {
      switch (key) {
        case 'LOGIN.OAUTH.NO_ACCOUNT_FOUND':
          return this.$t('LOGIN.OAUTH.NO_ACCOUNT_FOUND');
        case 'LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY':
          return this.$t('LOGIN.OAUTH.BUSINESS_ACCOUNTS_ONLY');
        case 'LOGIN.API.UNAUTH':
        default:
          return this.$t('LOGIN.API.UNAUTH');
      }
    },
    requestIdleCallbackPolyfill(callback) {
      if (window.requestIdleCallback) {
        window.requestIdleCallback(callback);
      } else {
        setTimeout(callback, 0);
      }
    },
    showAlertMessage(message) {
      this.loginApi.showLoading = false;
      this.loginApi.message = message;
      useAlert(this.loginApi.message);
    },
    handleImpersonation() {
      const urlParams = new URLSearchParams(window.location.search);
      const impersonation = urlParams.get(IMPERSONATION_URL_SEARCH_KEY);
      if (impersonation) {
        SessionStorage.set(SESSION_STORAGE_KEYS.IMPERSONATION_USER, true);
      }
    },
    submitLogin() {
      this.loginApi.hasErrored = false;
      this.loginApi.showLoading = true;
      const credentials = {
        email: this.email
          ? decodeURIComponent(this.email)
          : this.credentials.email,
        password: this.credentials.password,
        sso_auth_token: this.ssoAuthToken,
        ssoAccountId: this.ssoAccountId,
        ssoConversationId: this.ssoConversationId,
      };
      login(credentials)
        .then(result => {
          if (result?.mfaRequired) {
            this.loginApi.showLoading = false;
            this.mfaRequired = true;
            this.mfaToken = result.mfaToken;
            return;
          }
          this.handleImpersonation();
          this.showAlertMessage(this.$t('LOGIN.API.SUCCESS_MESSAGE'));
          window.location = '/app';
        })
        .catch(response => {
          if (this.email) {
            window.location = '/app/login';
          }
          this.loginApi.hasErrored = true;
          this.showAlertMessage(
            response?.message || this.$t('LOGIN.API.UNAUTH')
          );
        });
    },
    submitFormLogin() {
      if (this.v$.credentials.email.$invalid && !this.email) {
        this.showAlertMessage(this.$t('LOGIN.EMAIL.ERROR'));
        return;
      }
      this.submitLogin();
    },
    handleMfaVerified() {
      this.handleImpersonation();
      window.location = '/app';
    },
    handleMfaCancel() {
      this.mfaRequired = false;
      this.mfaToken = null;
      this.credentials.password = '';
    },
    async sendMagicLink() {
      if (!this.magicLinkEmail) return;
      this.magicLinkLoading = true;
      try {
        await axios.post('/agent_magic_link', { email: this.magicLinkEmail });
        this.magicLinkSent = true;
      } catch {
        useAlert('Something went wrong. Please try again.');
      } finally {
        this.magicLinkLoading = false;
      }
    },
    setTab(tab) {
      this.activeTab = tab;
      this.magicLinkSent = false;
      if (tab === 'magic') {
        this.magicLinkEmail = this.credentials.email || '';
      }
    },
    initConversationFlow() {
      const bar = document.getElementById('flow-progress-bar');
      const badge = document.getElementById('flow-badge');
      const statusText = document.getElementById('flow-status-text');
      if (!bar) return;

      const stages = [
        {
          badge: 'NEW',
          badgeBg: '#fef3c7',
          badgeColor: '#92400e',
          status: 'Customer message received...',
          progress: 0,
          active: -1,
        },
        {
          badge: 'ASSIGNED',
          badgeBg: '#dbeafe',
          badgeColor: '#1e40af',
          status: 'Assigning to support agent...',
          progress: 35,
          active: 0,
        },
        {
          badge: 'RESPONDING',
          badgeBg: '#ede9fe',
          badgeColor: '#6d28d9',
          status: 'Agent is responding...',
          progress: 70,
          active: 1,
        },
        {
          badge: 'RESOLVED',
          badgeBg: '#dcfce7',
          badgeColor: '#166534',
          status: 'Ticket resolved! ✓',
          progress: 100,
          active: 2,
        },
      ];

      let currentStage = -1;

      function setStepActive(idx) {
        for (let i = 0; i < 3; i++) {
          const step = document.getElementById('flow-step-' + i);
          const label = document.getElementById('flow-label-' + i);
          if (step) {
            const svg = step.querySelector('svg');
            if (i < idx) {
              step.style.background =
                'linear-gradient(135deg, #6366f1, #8b5cf6)';
              step.style.boxShadow = 'none';
              step.style.animation = 'none';
              if (svg) svg.setAttribute('fill', 'white');
              if (label) label.style.color = '#6366f1';
            } else if (i === idx) {
              step.style.background =
                'linear-gradient(135deg, #6366f1, #8b5cf6)';
              step.style.boxShadow = '0 0 0 4px rgba(99,102,241,0.2)';
              step.style.animation = 'sp-progress-pulse 1.5s infinite';
              if (svg) svg.setAttribute('fill', 'white');
              if (label) {
                label.style.color = '#6366f1';
                label.style.fontWeight = '800';
              }
            } else {
              step.style.background = '#e2e8f0';
              step.style.boxShadow = 'none';
              step.style.animation = 'none';
              if (svg) svg.setAttribute('fill', '#94a3b8');
              if (label) {
                label.style.color = '#94a3b8';
                label.style.fontWeight = '600';
              }
            }
          }
        }
        for (let j = 0; j < 2; j++) {
          const line = document.getElementById('flow-line-' + j);
          if (line) {
            line.style.background =
              j < idx ? 'linear-gradient(90deg, #6366f1, #8b5cf6)' : '#e2e8f0';
          }
        }
      }

      const advanceStage = () => {
        currentStage = (currentStage + 1) % stages.length;
        const s = stages[currentStage];
        if (bar) bar.style.width = s.progress + '%';
        if (badge) {
          badge.textContent = s.badge;
          badge.style.background = s.badgeBg;
          badge.style.color = s.badgeColor;
        }
        if (statusText) statusText.textContent = s.status;
        setStepActive(s.active);

        if (currentStage === stages.length - 1) {
          setTimeout(() => {
            if (bar) bar.style.width = '0%';
            setStepActive(-1);
            currentStage = -1;
            setTimeout(advanceStage, 800);
          }, 2500);
        } else {
          setTimeout(advanceStage, 2000);
        }
      };

      setTimeout(advanceStage, 600);
    },

    initRotatingStats() {
      const next = () => {
        this.statVisible = false;
        setTimeout(() => {
          this.statIndex = (this.statIndex + 1) % this.statsData.length;
          this.statVisible = true;
        }, 380);
        this.statsTimer = setTimeout(next, 3200);
      };
      this.statsTimer = setTimeout(next, 3200);
    },
  },
};
</script>

<template>
  <div class="sp-auth-page">
    <!-- Left visual panel -->
    <div class="sp-auth-visual">
      <div class="sp-grid-bg" />
      <div class="sp-glow-orb sp-glow-1" />
      <div class="sp-glow-orb sp-glow-2" />
      <div class="sp-glow-orb sp-glow-3" />
      <div class="sp-particle sp-particle-1" />
      <div class="sp-particle sp-particle-2" />
      <div class="sp-particle sp-particle-3" />
      <div class="sp-particle sp-particle-4" />
      <div class="sp-particle sp-particle-5" />

      <div class="sp-visual-inner">
        <!-- Badge -->
        <div class="sp-animate-in" style="margin-bottom: 16px">
          <div class="sp-badge">
            <span class="sp-badge-dot sp-badge-dot-indigo" />
            Trusted by 10,000+ businesses
          </div>
        </div>

        <!-- Hero text -->
        <div
          class="sp-animate-in sp-delay-1"
          style="text-align: center; margin-bottom: 8px"
        >
          <h1 class="sp-hero-text">
            Welcome<br />
            <span class="sp-gradient-text">Back.</span>
          </h1>
          <p class="sp-hero-sub">
            Your customers are waiting. Pick up right where you left off.
          </p>
        </div>

        <!-- Zignature-style rotating stats stage -->
        <div class="sp-stat-stage sp-animate-in sp-delay-3">
          <div class="sp-stat-solo sp-stat-solo-0">
            <div class="sp-stat-number" style="color: #6366f1">98.4%</div>
            <div class="sp-stat-name">Customer Satisfaction</div>
          </div>
          <div class="sp-stat-solo sp-stat-solo-1">
            <div class="sp-stat-number" style="color: #10b981">1M+</div>
            <div class="sp-stat-name">Conversations Handled</div>
          </div>
          <div class="sp-stat-solo sp-stat-solo-2">
            <div class="sp-stat-number" style="color: #3b82f6">847</div>
            <div class="sp-stat-name">Tickets Resolved Today</div>
          </div>
          <div class="sp-stat-solo sp-stat-solo-3">
            <div class="sp-stat-number" style="color: #f59e0b">12,483</div>
            <div class="sp-stat-name">Active Support Agents</div>
          </div>
          <div class="sp-stat-solo sp-stat-solo-4" style="padding-top: 44px">
            <div class="sp-stat-number" style="color: #ec4899">4.7/5</div>
            <div class="sp-stat-name">User Rating</div>
            <div class="sp-stat-review-logos">
              <!-- eslint-disable vue/no-useless-v-bind -->
              <img
                :src="'/g2-badges.png'"
                alt="G2 Spring 2025 awards"
                class="sp-stat-review-img"
              />
              <!-- eslint-enable vue/no-useless-v-bind -->
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right form panel -->
    <div class="sp-auth-form-side">
      <div class="sp-auth-form-inner-wrap">
        <div class="sp-auth-form-container">
          <!-- MFA Verification -->
          <section v-if="mfaRequired">
            <MfaVerification
              :mfa-token="mfaToken"
              @verified="handleMfaVerified"
              @cancel="handleMfaCancel"
            />
          </section>

          <!-- Regular Login -->
          <section v-else>
            <!-- Logo -->
            <div
              class="sp-animate-in"
              style="text-align: center; margin-bottom: 16px"
            >
              <img
                :src="globalConfig.logo"
                :alt="globalConfig.installationName"
                style="height: 56px; display: inline-block"
                class="dark:hidden"
              />
              <img
                v-if="globalConfig.logoDark"
                :src="globalConfig.logoDark"
                :alt="globalConfig.installationName"
                style="height: 56px; display: none"
                class="dark:inline-block"
              />
            </div>

            <div v-if="!email">
              <!-- Heading -->
              <div
                class="sp-animate-in sp-delay-1"
                style="text-align: center; margin-bottom: 14px"
              >
                <h2
                  style="
                    font-size: 22px;
                    font-weight: 800;
                    color: #0f172a;
                    letter-spacing: -0.03em;
                    margin-bottom: 3px;
                  "
                >
                  Sign In
                </h2>
                <p style="font-size: 13px; color: #64748b">
                  Sign In to Your Supportly Workspace.
                </p>
              </div>

              <!-- Tabs -->
              <div class="sp-animate-in sp-delay-2" style="margin-bottom: 12px">
                <div class="sp-login-tabs">
                  <button
                    type="button"
                    class="sp-login-tab"
                    :class="{ active: activeTab === 'password' }"
                    @click="setTab('password')"
                  >
                    Password
                  </button>
                  <button
                    type="button"
                    class="sp-login-tab"
                    :class="{ active: activeTab === 'magic' }"
                    @click="setTab('magic')"
                  >
                    Magic Link
                  </button>
                </div>
              </div>

              <!-- Social / SSO buttons — password tab only -->
              <template v-if="activeTab === 'password'">
                <div class="sp-animate-in sp-delay-2">
                  <a
                    v-if="showGoogleOAuth"
                    :href="googleAuthUrl"
                    class="sp-social-btn"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                        fill="#EA4335"
                      />
                    </svg>
                    <span style="color: #4f46e5">{{
                      $t('LOGIN.OAUTH.GOOGLE_LOGIN')
                    }}</span>
                  </a>
                  <div v-if="showSamlLogin" style="margin-top: 8px">
                    <router-link to="/app/login/sso" class="sp-social-btn">
                      <svg
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="#6366f1"
                        stroke-width="2"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      <span style="color: #4f46e5">{{
                        $t('LOGIN.SAML.LABEL')
                      }}</span>
                    </router-link>
                  </div>
                </div>

                <div
                  v-if="showGoogleOAuth || showSamlLogin"
                  class="sp-divider sp-animate-in sp-delay-2"
                >
                  <span>Or Continue with Email</span>
                </div>
              </template>

              <!-- Magic Link sent -->
              <div
                v-if="activeTab === 'magic' && magicLinkSent"
                class="sp-animate-in"
                style="text-align: center; padding: 24px 0"
              >
                <div
                  style="
                    width: 56px;
                    height: 56px;
                    background: linear-gradient(135deg, #ede9fe, #ddd6fe);
                    border-radius: 18px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 16px;
                  "
                >
                  <svg
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="#6366f1"
                    stroke-width="1.5"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p
                  style="
                    font-size: 16px;
                    font-weight: 700;
                    color: #0f172a;
                    margin-bottom: 4px;
                  "
                >
                  Check your inbox
                </p>
                <p style="font-size: 13px; color: #64748b; margin-bottom: 20px">
                  We sent a sign-in link to <strong>{{ magicLinkEmail }}</strong
                  >.<br />It expires in 15 minutes.
                </p>
                <button
                  type="button"
                  class="sp-text-link"
                  @click="setTab('password')"
                >
                  ← Back to Sign In
                </button>
              </div>

              <!-- Magic Link form -->
              <form
                v-else-if="activeTab === 'magic'"
                class="sp-animate-in sp-delay-3"
                @submit.prevent="sendMagicLink"
              >
                <div class="sp-field">
                  <label class="sp-label">Email Address</label>
                  <input
                    v-model="magicLinkEmail"
                    type="email"
                    class="sp-input"
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <button
                  type="submit"
                  class="sp-btn"
                  :disabled="magicLinkLoading"
                  style="margin-top: 4px"
                >
                  <span v-if="!magicLinkLoading">Send Magic Link</span>
                  <span v-else>Sending...</span>
                </button>
                <button
                  type="button"
                  class="sp-text-link"
                  style="width: 100%; margin-top: 10px"
                  @click="setTab('password')"
                >
                  ← Back to Password Sign In
                </button>
              </form>

              <!-- Password login form -->
              <form
                v-else
                class="sp-animate-in sp-delay-3"
                :class="{ 'animate-wiggle': loginApi.hasErrored }"
                @submit.prevent="submitFormLogin"
              >
                <div class="sp-field">
                  <label class="sp-label">{{ $t('LOGIN.EMAIL.LABEL') }}</label>
                  <input
                    v-model="credentials.email"
                    name="email_address"
                    type="text"
                    autocomplete="username"
                    class="sp-input"
                    :class="{ 'sp-input-error': v$.credentials.email.$error }"
                    :tabindex="1"
                    :placeholder="$t('LOGIN.EMAIL.PLACEHOLDER')"
                    @input="v$.credentials.email.$touch"
                  />
                </div>
                <div class="sp-field" style="margin-top: 12px">
                  <div
                    style="
                      display: flex;
                      justify-content: space-between;
                      align-items: center;
                      margin-bottom: 8px;
                    "
                  >
                    <label class="sp-label" style="margin-bottom: 0">{{
                      $t('LOGIN.PASSWORD.LABEL')
                    }}</label>
                    <router-link
                      v-if="!globalConfig.disableUserProfileUpdate"
                      to="auth/reset/password"
                      class="sp-link"
                      style="font-size: 13px"
                      :tabindex="4"
                    >
                      {{ $t('LOGIN.FORGOT_PASSWORD') }}
                    </router-link>
                  </div>
                  <input
                    v-model="credentials.password"
                    type="password"
                    name="password"
                    autocomplete="current-password"
                    class="sp-input"
                    :class="{
                      'sp-input-error': v$.credentials.password.$error,
                    }"
                    :tabindex="2"
                    :placeholder="$t('LOGIN.PASSWORD.PLACEHOLDER')"
                    @input="v$.credentials.password.$touch"
                  />
                </div>
                <button
                  type="submit"
                  class="sp-btn"
                  :disabled="loginApi.showLoading"
                  :tabindex="3"
                  style="margin-top: 16px"
                >
                  <span v-if="!loginApi.showLoading">{{
                    $t('LOGIN.SUBMIT')
                  }}</span>
                  <span v-else>Signing in...</span>
                </button>
              </form>

              <!-- Sign up link -->
              <p
                v-if="
                  showSignupLink && !(activeTab === 'magic' && magicLinkSent)
                "
                class="sp-animate-in sp-delay-5"
                style="
                  text-align: center;
                  margin-top: 16px;
                  padding-top: 12px;
                  border-top: 1px solid #f1f5f9;
                  font-size: 12px;
                  color: #64748b;
                "
              >
                Don't Have an Account?
                <router-link
                  to="/app/auth/signup"
                  class="sp-link"
                  style="margin-left: 4px; font-weight: 700"
                >
                  {{ replaceInstallationName($t('LOGIN.CREATE_NEW_ACCOUNT')) }}
                  →
                </router-link>
              </p>
            </div>

            <!-- SSO auto-login spinner -->
            <div
              v-else
              style="
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 64px 0;
              "
            >
              <Spinner color-scheme="primary" size="" />
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes sp-particle-float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
    opacity: 0.4;
  }
  25% {
    transform: translate(20px, -30px) scale(1.5);
    opacity: 0.8;
  }
  50% {
    transform: translate(-10px, -60px) scale(1);
    opacity: 0.6;
  }
  75% {
    transform: translate(15px, -20px) scale(1.3);
    opacity: 0.9;
  }
}
@keyframes sp-float-drift {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  33% {
    transform: translate(30px, -20px) scale(1.05);
  }
  66% {
    transform: translate(-20px, 15px) scale(0.95);
  }
}
@keyframes sp-slide-in {
  from {
    opacity: 0;
    transform: translateY(32px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes sp-pulse-soft {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}
@keyframes sp-shimmer-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}
@keyframes sp-progress-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(99, 102, 241, 0);
  }
}

.sp-auth-page {
  display: flex;
  height: 100vh;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
}

.sp-auth-visual {
  display: none;
  width: 50%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    145deg,
    #f5f3ff 0%,
    #ede9fe 25%,
    #e0e7ff 50%,
    #ddd6fe 75%,
    #f0f9ff 100%
  );
}

@media (min-width: 1024px) {
  .sp-auth-visual {
    display: flex;
  }
}

.sp-auth-form-side {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
  background: #ffffff;
}

.sp-auth-form-inner-wrap {
  min-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  box-sizing: border-box;
}

@media (min-width: 1024px) {
  .sp-auth-form-inner-wrap {
    padding: 32px 48px;
  }
}
@media (min-width: 1280px) {
  .sp-auth-form-inner-wrap {
    padding: 32px 64px;
  }
}

.sp-auth-form-container {
  width: 100%;
  max-width: 420px;
}

.sp-grid-bg {
  position: absolute;
  inset: 0;
  background-image: linear-gradient(
      rgba(99, 102, 241, 0.05) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
  background-size: 40px 40px;
}

.sp-glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
}
.sp-glow-1 {
  width: 500px;
  height: 500px;
  background: rgba(139, 92, 246, 0.3);
  top: -120px;
  right: -100px;
  animation: sp-float-drift 8s ease-in-out infinite;
}
.sp-glow-2 {
  width: 450px;
  height: 450px;
  background: rgba(99, 102, 241, 0.25);
  bottom: -60px;
  left: -80px;
  animation: sp-float-drift 10s ease-in-out infinite reverse;
}
.sp-glow-3 {
  width: 300px;
  height: 300px;
  background: rgba(59, 130, 246, 0.2);
  top: 35%;
  left: 25%;
  animation: sp-float-drift 12s ease-in-out infinite 2s;
}

.sp-particle {
  position: absolute;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.4);
  pointer-events: none;
}
.sp-particle-1 {
  width: 6px;
  height: 6px;
  top: 15%;
  left: 20%;
  animation: sp-particle-float 6s ease-in-out infinite;
}
.sp-particle-2 {
  width: 4px;
  height: 4px;
  top: 35%;
  right: 15%;
  animation: sp-particle-float 8s ease-in-out infinite 1s;
}
.sp-particle-3 {
  width: 8px;
  height: 8px;
  bottom: 25%;
  left: 30%;
  animation: sp-particle-float 7s ease-in-out infinite 2s;
}
.sp-particle-4 {
  width: 5px;
  height: 5px;
  top: 60%;
  right: 25%;
  animation: sp-particle-float 9s ease-in-out infinite 0.5s;
}
.sp-particle-5 {
  width: 3px;
  height: 3px;
  top: 80%;
  left: 15%;
  animation: sp-particle-float 5s ease-in-out infinite 3s;
}

.sp-visual-inner {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 28px 36px;
}

.sp-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 100px;
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  border: 1px solid rgba(99, 102, 241, 0.15);
  backdrop-filter: blur(12px);
}
.sp-badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}
.sp-badge-dot-indigo {
  background: linear-gradient(135deg, #6366f1, #a855f7);
  animation: sp-pulse-soft 2s infinite;
}

.sp-hero-text {
  font-size: 60px;
  font-weight: 900;
  line-height: 1;
  color: #0f172a;
  letter-spacing: -0.04em;
  margin-bottom: 10px;
}
.sp-hero-sub {
  font-size: 15px;
  color: #64748b;
  line-height: 1.4;
  max-width: 360px;
  margin: 0 auto;
}
.sp-gradient-text {
  background: linear-gradient(135deg, #4f46e5, #7c3aed, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.sp-glass-card {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.04),
    0 1px 2px rgba(0, 0, 0, 0.02),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
}

.sp-icon-gradient {
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.sp-counter-value {
  font-size: 18px;
  font-weight: 900;
  background: linear-gradient(135deg, #6366f1, #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.03em;
}

.sp-trust-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  padding: 5px 12px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 100px;
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.8);
}

.sp-animate-in {
  animation: sp-slide-in 0.7s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0;
}
.sp-delay-1 {
  animation-delay: 0.1s;
}
.sp-delay-2 {
  animation-delay: 0.2s;
}
.sp-delay-3 {
  animation-delay: 0.3s;
}
.sp-delay-4 {
  animation-delay: 0.4s;
}
.sp-delay-5 {
  animation-delay: 0.5s;
}

/* Form right panel */
.sp-login-tabs {
  display: flex;
  background: #f1f5f9;
  border-radius: 14px;
  padding: 4px;
  gap: 4px;
}
.sp-login-tab {
  flex: 1;
  padding: 8px 14px;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
}
.sp-login-tab.active {
  background: #ffffff;
  color: #0f172a;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}
.sp-login-tab:hover:not(.active) {
  color: #334155;
}

.sp-social-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 10px 20px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
}
.sp-social-btn:hover {
  border-color: #c7d2fe;
  background: #fafaff;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.08);
  transform: translateY(-1px);
}

.sp-divider {
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 14px 0;
}
.sp-divider::before,
.sp-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e2e8f0;
}
.sp-divider span {
  font-size: 13px;
  font-weight: 500;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.sp-field {
  display: flex;
  flex-direction: column;
}
.sp-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}
.sp-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  background: #ffffff;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
}
.sp-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.12);
}
.sp-input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}
.sp-input-error {
  border-color: #f43f5e;
  box-shadow: 0 0 0 4px rgba(244, 63, 94, 0.1);
}

.sp-btn {
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #4f46e5 0%, #6366f1 50%, #7c3aed 100%);
  color: #ffffff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    'Inter',
    -apple-system,
    sans-serif;
  letter-spacing: -0.01em;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.3);
}
.sp-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.45);
}
.sp-btn:active {
  transform: translateY(0);
}
.sp-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}
.sp-btn::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.15),
    transparent
  );
  animation: sp-shimmer-slide 3s infinite;
}

.sp-link {
  color: #6366f1;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.2s;
}
.sp-link:hover {
  color: #4f46e5;
}

.sp-text-link {
  background: none;
  border: none;
  font-size: 13px;
  color: #94a3b8;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
  font-family: inherit;
}
.sp-text-link:hover {
  color: #475569;
}

/* ── Zignature-style rotating stats ── */
.sp-stat-stage {
  position: relative;
  width: 100%;
  max-width: 400px;
  height: 280px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0;
}
.sp-stat-solo {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: 0;
  will-change: transform, opacity, filter;
}
.sp-stat-number {
  font-size: 118px;
  font-weight: 900;
  letter-spacing: -0.05em;
  line-height: 1;
  margin-bottom: 8px;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.06);
}
.sp-stat-name {
  font-size: 16px;
  color: #475569;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.sp-stat-review-logos {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
}
.sp-stat-review-img {
  max-width: 320px;
  width: 100%;
  height: auto;
  opacity: 0.92;
}
.sp-stat-solo-0 {
  animation: sp-stat-0 17.5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}
.sp-stat-solo-1 {
  animation: sp-stat-1 17.5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}
.sp-stat-solo-2 {
  animation: sp-stat-2 17.5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}
.sp-stat-solo-3 {
  animation: sp-stat-3 17.5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}
.sp-stat-solo-4 {
  animation: sp-stat-4 17.5s cubic-bezier(0.22, 1, 0.36, 1) infinite;
}

@keyframes sp-stat-0 {
  0% {
    opacity: 0;
    transform: translate(-120px, 0) scale(0.6) rotate(-12deg);
    filter: blur(12px);
  }
  1% {
    opacity: 0.7;
    transform: translate(0, 0) scale(1.06) rotate(0deg);
    filter: blur(1px);
  }
  4% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  16% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  17.6% {
    opacity: 0.7;
    transform: scale(1.04) rotate(0deg);
    filter: blur(1px);
  }
  20% {
    opacity: 0;
    transform: translate(120px, 0) scale(0.6) rotate(12deg);
    filter: blur(12px);
  }
  100% {
    opacity: 0;
    transform: translate(120px, 0) scale(0.6) rotate(12deg);
    filter: blur(12px);
  }
}
@keyframes sp-stat-1 {
  0% {
    opacity: 0;
    transform: translate(0, 100px) scale(0.6) rotate(8deg);
    filter: blur(12px);
  }
  20% {
    opacity: 0;
    transform: translate(0, 100px) scale(0.6) rotate(8deg);
    filter: blur(12px);
  }
  21% {
    opacity: 0.7;
    transform: translate(0, 0) scale(1.06) rotate(0deg);
    filter: blur(1px);
  }
  24% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  36% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  37.6% {
    opacity: 0.7;
    transform: scale(1.04) rotate(0deg);
    filter: blur(1px);
  }
  40% {
    opacity: 0;
    transform: translate(0, -100px) scale(0.6) rotate(-8deg);
    filter: blur(12px);
  }
  100% {
    opacity: 0;
    transform: translate(0, -100px) scale(0.6) rotate(-8deg);
    filter: blur(12px);
  }
}
@keyframes sp-stat-2 {
  0% {
    opacity: 0;
    transform: translate(120px, 0) scale(0.6) rotate(12deg);
    filter: blur(12px);
  }
  40% {
    opacity: 0;
    transform: translate(120px, 0) scale(0.6) rotate(12deg);
    filter: blur(12px);
  }
  41% {
    opacity: 0.7;
    transform: translate(0, 0) scale(1.06) rotate(0deg);
    filter: blur(1px);
  }
  44% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  56% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  57.6% {
    opacity: 0.7;
    transform: scale(1.04) rotate(0deg);
    filter: blur(1px);
  }
  60% {
    opacity: 0;
    transform: translate(-120px, 0) scale(0.6) rotate(-12deg);
    filter: blur(12px);
  }
  100% {
    opacity: 0;
    transform: translate(-120px, 0) scale(0.6) rotate(-12deg);
    filter: blur(12px);
  }
}
@keyframes sp-stat-3 {
  0% {
    opacity: 0;
    transform: translate(0, -100px) scale(0.6) rotate(-8deg);
    filter: blur(12px);
  }
  60% {
    opacity: 0;
    transform: translate(0, -100px) scale(0.6) rotate(-8deg);
    filter: blur(12px);
  }
  61% {
    opacity: 0.7;
    transform: translate(0, 0) scale(1.06) rotate(0deg);
    filter: blur(1px);
  }
  64% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  76% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  77.6% {
    opacity: 0.7;
    transform: scale(1.04) rotate(0deg);
    filter: blur(1px);
  }
  80% {
    opacity: 0;
    transform: translate(0, 100px) scale(0.6) rotate(8deg);
    filter: blur(12px);
  }
  100% {
    opacity: 0;
    transform: translate(0, 100px) scale(0.6) rotate(8deg);
    filter: blur(12px);
  }
}
@keyframes sp-stat-4 {
  0% {
    opacity: 0;
    transform: translate(-80px, 80px) scale(0.6) rotate(10deg);
    filter: blur(12px);
  }
  80% {
    opacity: 0;
    transform: translate(-80px, 80px) scale(0.6) rotate(10deg);
    filter: blur(12px);
  }
  81% {
    opacity: 0.7;
    transform: translate(0, 0) scale(1.06) rotate(0deg);
    filter: blur(1px);
  }
  84% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  96% {
    opacity: 1;
    transform: translate(0, 0) scale(1) rotate(0deg);
    filter: blur(0);
  }
  97.6% {
    opacity: 0.7;
    transform: scale(1.04) rotate(0deg);
    filter: blur(1px);
  }
  100% {
    opacity: 0;
    transform: translate(80px, -80px) scale(0.6) rotate(-10deg);
    filter: blur(12px);
  }
}
</style>
